// The OTA gate controls: the master kill switch, apply-mode, an optional
// native-build floor override, and release notes. Which bundle is live is chosen
// in the history list above; Save here only writes these flags (keeping the
// current live pointer + upload history intact).
import { Switch } from '../../../components/ui/Switch';
import { cn } from '../../../lib/cn';
import { Field, inputClass } from './fields';
import type { OtaAdmin } from './useOtaAdmin';

export function OtaControls({ admin }: { admin: OtaAdmin }) {
  const {
    enabled,
    setEnabled,
    mandatory,
    setMandatory,
    minNativeBuild,
    setMinNativeBuild,
    notes,
    setNotes,
    save,
    saveState,
    saveError,
    blocked,
    hasLiveBundle,
    stored
  } = admin;

  return (
    <div className="rounded-2xl border border-black/5 bg-card p-4 shadow-tile">
      <div className="flex items-center justify-between gap-3 rounded-xl border border-black/10 bg-wash px-3.5 py-2.5">
        <div>
          <div className="text-[14px] font-bold text-ink">Live updates enabled</div>
          <div className="text-[12px] text-ink3">The master kill switch. Off = nobody updates.</div>
        </div>
        <Switch checked={enabled} onChange={setEnabled} label="Enable live updates" />
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Field
          label="Minimum native build"
          hint="iOS CFBundleVersion floor. Blank = use the live bundle's own floor."
        >
          <input
            className={inputClass}
            inputMode="numeric"
            placeholder={stored?.minNativeBuild != null ? String(stored.minNativeBuild) : 'e.g. 12'}
            value={minNativeBuild}
            onChange={(e) => setMinNativeBuild(e.target.value.replace(/[^0-9]/g, ''))}
          />
        </Field>
        <div className="flex items-end">
          <div className="flex w-full items-center justify-between gap-3 rounded-xl border border-black/10 bg-wash px-3.5 py-2.5">
            <div>
              <div className="text-[14px] font-bold text-ink">Apply immediately</div>
              <div className="text-[12px] text-ink3">Off = applies on next app open.</div>
            </div>
            <Switch checked={mandatory} onChange={setMandatory} label="Apply immediately" />
          </div>
        </div>
      </div>

      <div className="mt-4">
        <Field label="Release notes" hint="Admin-facing only. Optional.">
          <textarea
            className={cn(inputClass, 'min-h-[68px] resize-y')}
            placeholder="e.g. Fix card builder boost label on iOS"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </div>

      {blocked && (
        <p className="mt-3 text-[12px] font-semibold text-danger">
          Point the release at an uploaded bundle (in the history above) before enabling updates.
        </p>
      )}
      {!hasLiveBundle && !blocked && (
        <p className="mt-3 text-[12px] text-ink3">
          No live bundle selected yet — use “Set live” on a release above.
        </p>
      )}
      {saveState === 'error' && saveError && (
        <p className="mt-3 text-[12px] font-semibold text-danger">{saveError}</p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saveState === 'saving' || blocked}
          className={cn(
            'tap rounded-2xl bg-night px-5 py-3 text-[14px] font-bold text-white',
            (saveState === 'saving' || blocked) && 'opacity-50'
          )}
        >
          {saveState === 'saving' ? 'Saving…' : 'Save gate settings'}
        </button>
        {saveState === 'saved' && (
          <span className="text-[13px] font-semibold text-accent-ink">Saved</span>
        )}
      </div>
    </div>
  );
}
